import React, { useState, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Calendar,
  MapPin,
  BookOpen,
  Users,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  Filter,
} from "lucide-react";
import toast from "react-hot-toast";
import { AuthContext } from "../../contexts/AuthContext";
import { getClasses } from "../../services/classes";
import { getStudents } from "../../services/students";
import { getMyTeacherProfile } from "../../services/teachers";

type DayOfWeek =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

interface SchedulePeriod {
  day: DayOfWeek;
  startTime: string;
  endTime: string;
  subjectName: string;
  teacherId: string;
  room?: string;
  studentIds?: string[];
}

interface TeacherSchedulePeriod extends SchedulePeriod {
  className: string;
  classId: string;
  classStudentIds: string[];
}

interface PeriodStudentListItem {
  _id: string;
  name: string;
  admissionId: string;
}

const DAYS_OF_WEEK: DayOfWeek[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const DAY_NAMES: Record<DayOfWeek, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

const TIME_SLOTS = [
  "08:00",
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
  "18:00",
];

const TeacherSchedule: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const [selectedDay, setSelectedDay] = useState<DayOfWeek | "all">("all");
  const [currentWeek, setCurrentWeek] = useState(0);
  const [selectedPeriodForStudents, setSelectedPeriodForStudents] =
    useState<TeacherSchedulePeriod | null>(null);

  // Fetch current teacher profile to get the Teacher document _id
  const {
    data: myProfileData,
    isLoading: profileLoading,
    error: profileError,
  } = useQuery({
    queryKey: ["my-teacher-profile"],
    queryFn: getMyTeacherProfile,
    enabled: !!user?.id,
  });

  const myTeacherId = myProfileData?.data?._id;

  // Fetch all classes (with schedule periods) once we know the teacher _id
  const {
    data: classesData,
    isLoading: classesLoading,
    error: classesError,
  } = useQuery({
    queryKey: ["all-classes-for-schedule"],
    queryFn: () => getClasses({ limit: 500 }),
    enabled: !!myTeacherId,
  });

  const isLoading = profileLoading || classesLoading;
  const error = profileError || classesError;

  const { data: periodStudentsData, isLoading: periodStudentsLoading } =
    useQuery({
      queryKey: ["period-students", selectedPeriodForStudents?.classId],
      queryFn: () => {
        if (!selectedPeriodForStudents?.classId) {
          return Promise.resolve({
            success: true,
            data: [],
            pagination: {
              currentPage: 1,
              totalPages: 1,
              totalItems: 0,
              hasNext: false,
              hasPrev: false,
            },
          });
        }

        return getStudents({ limit: 1000 });
      },
      enabled: !!selectedPeriodForStudents?.classId,
    });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-emerald-500"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading schedule...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const errMsg =
      (error as any)?.response?.data?.message ||
      (error as any)?.message ||
      "Unable to load your schedule";
    toast.error(errMsg);
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 flex items-center justify-center">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 max-w-md">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-300 to-pink-400 flex items-center justify-center mx-auto mb-4">
            <Calendar className="h-6 w-6 text-white" />
          </div>
          <h3 className="text-slate-900 font-bold text-center text-lg">
            Error Loading Schedule
          </h3>
          <p className="text-slate-600 font-medium mt-2 text-center">
            {errMsg}
          </p>
        </div>
      </div>
    );
  }

  // All classes with their schedule periods
  const allClasses = classesData?.data || [];

  const normalizeId = (value: any): string => {
    if (!value) return "";
    if (typeof value === "object") {
      return String(value._id || value.studentId || value.userId || "");
    }
    return String(value);
  };

  // Collect only periods assigned to this teacher
  const teacherPeriods: TeacherSchedulePeriod[] = [];

  allClasses.forEach((classItem: any) => {
    const classStudentIds = (classItem.students || [])
      .map((studentRef: any) =>
        normalizeId(studentRef?.studentId || studentRef?._id || studentRef),
      )
      .filter(Boolean);

    const periods: any[] = classItem.schedule?.periods || [];
    periods.forEach((period: any) => {
      if (String(period.teacherId) === String(myTeacherId)) {
        teacherPeriods.push({
          day: period.day,
          startTime: period.startTime,
          endTime: period.endTime,
          subjectName: period.subjectName,
          teacherId: period.teacherId,
          room: period.room,
          studentIds: period.studentIds || [],
          className: classItem.name,
          classId: classItem._id,
          classStudentIds,
        });
      }
    });
  });

  const periodStudents = (periodStudentsData?.data || []) as any[];

  const selectedStudentIds = new Set(
    (selectedPeriodForStudents?.studentIds || [])
      .map((id) => normalizeId(id))
      .filter(Boolean),
  );

  const getStudentIdentityValues = (student: any): string[] => {
    return [
      normalizeId(student?._id),
      normalizeId(student?.userId),
      normalizeId(student?.studentId),
      normalizeId(student?.admissionNumber),
    ].filter(Boolean);
  };

  const getStudentName = (student: any) => {
    const firstName = student?.userInfo?.firstName || student?.firstName || "";
    const lastName = student?.userInfo?.lastName || student?.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || "Unnamed student";
  };

  const getStudentAdmissionId = (student: any) => {
    return student?.admissionNumber || student?.studentId || "N/A";
  };

  const modalStudents: PeriodStudentListItem[] = periodStudents
    .filter((student: any) => {
      const fallbackClassStudentIds = new Set(
        (selectedPeriodForStudents?.classStudentIds || [])
          .map((id) => normalizeId(id))
          .filter(Boolean),
      );

      const targetIds =
        selectedStudentIds.size > 0
          ? selectedStudentIds
          : fallbackClassStudentIds;

      if (targetIds.size === 0) {
        return true;
      }

      return getStudentIdentityValues(student).some((id) => targetIds.has(id));
    })
    .map((student: any) => ({
      _id: String(student._id),
      name: getStudentName(student),
      admissionId: getStudentAdmissionId(student),
    }));

  const openStudentsModal = (period: TeacherSchedulePeriod) => {
    setSelectedPeriodForStudents(period);
  };

  const closeStudentsModal = () => {
    setSelectedPeriodForStudents(null);
  };

  // Filter periods by selected day
  const filteredPeriods =
    selectedDay === "all"
      ? teacherPeriods
      : teacherPeriods.filter((period) => period.day === selectedDay);

  // Group periods by day for calendar view
  const periodsByDay = DAYS_OF_WEEK.reduce(
    (acc, day) => {
      acc[day] = teacherPeriods.filter((period) => period.day === day);
      return acc;
    },
    {} as Record<DayOfWeek, typeof teacherPeriods>,
  );

  // Get subject color
  const getSubjectColor = (subject: string) => {
    const colors = [
      "bg-gradient-to-br from-blue-300 to-indigo-400 text-white",
      "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white",
      "bg-gradient-to-br from-purple-300 to-purple-400 text-white",
      "bg-gradient-to-br from-amber-300 to-yellow-400 text-white",
      "bg-gradient-to-br from-red-300 to-pink-400 text-white",
      "bg-gradient-to-br from-blue-300 to-purple-400 text-white",
    ];
    const index = subject.length % colors.length;
    return colors[index];
  };

  // Get current week dates
  const getCurrentWeekDates = () => {
    const today = new Date();
    const currentDay = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - currentDay + 1 + currentWeek * 7);

    return DAYS_OF_WEEK.map((_, index) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + index);
      return date;
    });
  };

  const weekDates = getCurrentWeekDates();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-3 sm:p-4 lg:p-6">
      {/* Header */}
      <div className="mb-6 lg:mb-8 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
          My Schedule
        </h1>
        <p className="text-slate-600 mt-2 text-sm sm:text-base font-medium">
          View your teaching schedule and assignments
        </p>
      </div>

      {/* Week Navigation */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-3 sm:p-4 mb-4 lg:mb-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentWeek(currentWeek - 1)}
            className="flex items-center space-x-2 px-2 sm:px-3 py-2 text-slate-700 hover:text-slate-900 bg-white border border-slate-200 hover:shadow-lg rounded-xl transition-all duration-300 hover:scale-105 text-sm font-bold"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Previous Week</span>
            <span className="sm:hidden">Prev</span>
          </button>

          <div className="text-center">
            <h3 className="text-sm sm:text-lg font-bold text-slate-900">
              {weekDates[0].toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}{" "}
              -{" "}
              {weekDates[6].toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </h3>
          </div>

          <button
            onClick={() => setCurrentWeek(currentWeek + 1)}
            className="flex items-center space-x-2 px-2 sm:px-3 py-2 text-slate-700 hover:text-slate-900 bg-white border border-slate-200 hover:shadow-lg rounded-xl transition-all duration-300 hover:scale-105 text-sm font-bold"
          >
            <span className="hidden sm:inline">Next Week</span>
            <span className="sm:hidden">Next</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-3 sm:p-4 mb-4 lg:mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-300 to-indigo-400 flex items-center justify-center shadow-sm">
              <Filter className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-bold text-slate-900">
              Filter by day:
            </span>
          </div>
          <select
            value={selectedDay}
            onChange={(e) =>
              setSelectedDay(e.target.value as DayOfWeek | "all")
            }
            className="px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-medium bg-white shadow-sm"
          >
            <option value="all">All Days</option>
            {DAYS_OF_WEEK.map((day) => (
              <option key={day} value={day}>
                {DAY_NAMES[day]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Weekly Calendar View - Horizontally Scrollable */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg mb-4 lg:mb-6">
        <div className="p-3 sm:p-4 border-b border-slate-200/60">
          <h3 className="text-lg font-bold text-slate-900">Weekly Schedule</h3>
          <p className="text-sm text-slate-600 font-medium mt-1">
            Scroll horizontally to view all days
          </p>
        </div>

        <div className="overflow-x-auto">
          <div className="p-3 sm:p-4">
            <div className="grid grid-cols-8 gap-1 min-w-[800px]">
              {/* Ensures minimum width for better readability */}
              {/* Header row */}
              <div className="p-2 text-center font-bold text-slate-900 bg-gradient-to-r from-slate-50 to-blue-50 text-sm rounded-lg">
                Time
              </div>
              {DAYS_OF_WEEK.map((day, index) => (
                <div
                  key={day}
                  className="p-2 text-center font-bold text-slate-900 bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg"
                >
                  <div className="text-sm">{DAY_NAMES[day]}</div>
                  <div className="text-xs text-slate-600 font-semibold">
                    {weekDates[index].toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </div>
              ))}

              {/* Time slots */}
              {TIME_SLOTS.map((timeSlot) => (
                <React.Fragment key={timeSlot}>
                  <div className="p-2 text-center text-sm font-bold text-slate-700 bg-gradient-to-r from-slate-50 to-blue-50 border-r border-b border-slate-200/60 rounded-lg">
                    {timeSlot}
                  </div>
                  {DAYS_OF_WEEK.map((day) => {
                    const dayPeriods = periodsByDay[day] || [];

                    // Find period that starts at this time slot
                    const startingPeriod = dayPeriods.find(
                      (p) => p.startTime === timeSlot,
                    );

                    return (
                      <div
                        key={`${day}-${timeSlot}`}
                        className="p-1 border-r border-b border-slate-200/60 min-h-[60px] relative overflow-hidden hover:bg-slate-50 transition-colors"
                      >
                        {startingPeriod && (
                          <div
                            title={[
                              startingPeriod.subjectName,
                              startingPeriod.className,
                              `${startingPeriod.startTime} – ${startingPeriod.endTime}`,
                              startingPeriod.room
                                ? `Room: ${startingPeriod.room}`
                                : null,
                            ]
                              .filter(Boolean)
                              .join("\n")}
                            className={`p-2 rounded-xl text-xs shadow-md h-[calc(100%-8px)] overflow-hidden cursor-default ${getSubjectColor(
                              startingPeriod.subjectName,
                            )}`}
                            style={{
                              position: "absolute",
                              top: "4px",
                              left: "4px",
                              right: "4px",
                              zIndex: 10,
                            }}
                          >
                            <div className="font-bold truncate">
                              {startingPeriod.subjectName}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Periods List - Mobile Friendly */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg mt-4 lg:mt-6">
        <div className="p-3 sm:p-4 border-b border-slate-200/60">
          <h3 className="text-lg font-bold text-slate-900">
            {selectedDay === "all"
              ? "All Classes"
              : `${DAY_NAMES[selectedDay as DayOfWeek]} Classes`}
          </h3>
        </div>

        <div className="p-3 sm:p-4">
          {filteredPeriods.length > 0 ? (
            <div className="space-y-3">
              {filteredPeriods.map((period, index) => (
                <div
                  key={index}
                  className="border border-slate-200/60 rounded-xl p-3 sm:p-4 bg-white hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 space-y-2 sm:space-y-0">
                    <div
                      className={`px-3 py-1.5 rounded-xl text-sm font-bold self-start shadow-sm ${getSubjectColor(
                        period.subjectName,
                      )}`}
                    >
                      {DAY_NAMES[period.day]}
                    </div>
                    <div className="text-left sm:text-right">
                      <div className="text-sm font-bold text-slate-900">
                        {period.startTime} - {period.endTime}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center shadow-sm">
                        <BookOpen className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-sm font-bold text-slate-900">
                        {period.subjectName}
                      </span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center shadow-sm">
                        <Users className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-sm text-slate-600 font-medium">
                        {period.className}
                      </span>
                    </div>

                    {period.room && (
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-300 to-purple-400 flex items-center justify-center shadow-sm">
                          <MapPin className="h-4 w-4 text-white" />
                        </div>
                        <span className="text-sm text-slate-600 font-medium">
                          Room: {period.room}
                        </span>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => openStudentsModal(period)}
                      className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 hover:shadow-sm transition-all"
                    >
                      <Eye className="h-4 w-4" />
                      View Students
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Calendar className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                No classes scheduled
              </h3>
              <p className="text-slate-600 font-medium">
                {selectedDay === "all"
                  ? "You don't have any classes assigned yet."
                  : `No classes scheduled for ${
                      DAY_NAMES[selectedDay as DayOfWeek]
                    }.`}
              </p>
            </div>
          )}
        </div>
      </div>

      {selectedPeriodForStudents && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/45 backdrop-blur-sm p-3 sm:p-6 flex items-center justify-center"
          onClick={closeStudentsModal}
        >
          <div
            className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-4 sm:px-6 py-4 border-b border-slate-200/60 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                  Students in Period
                </h3>
                <p className="text-sm text-slate-600 font-medium mt-1">
                  {selectedPeriodForStudents.subjectName} •{" "}
                  {selectedPeriodForStudents.className}
                </p>
                <p className="text-xs text-slate-500 font-semibold mt-0.5">
                  {DAY_NAMES[selectedPeriodForStudents.day]} •{" "}
                  {selectedPeriodForStudents.startTime} -{" "}
                  {selectedPeriodForStudents.endTime}
                </p>
              </div>

              <button
                type="button"
                onClick={closeStudentsModal}
                className="h-9 w-9 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors flex items-center justify-center"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto max-h-[70vh]">
              {periodStudentsLoading ? (
                <div className="py-12 flex flex-col items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-t-2 border-emerald-500"></div>
                  <p className="mt-3 text-sm text-slate-600 font-medium">
                    Loading students...
                  </p>
                </div>
              ) : modalStudents.length > 0 ? (
                <div className="space-y-2">
                  {modalStudents.map((student) => (
                    <div
                      key={student._id}
                      className="flex items-center justify-between rounded-xl border border-slate-200/60 bg-white p-3"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center shadow-sm">
                          <Users className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-900">
                            {student.name}
                          </p>
                        </div>
                      </div>

                      <span className="text-xs sm:text-sm text-slate-600 font-semibold px-2.5 py-1 rounded-lg bg-slate-100">
                        {student.admissionId}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center mx-auto mb-4 shadow-md">
                    <Users className="h-7 w-7 text-white" />
                  </div>
                  <h4 className="text-base font-bold text-slate-900 mb-1">
                    No students found
                  </h4>
                  <p className="text-sm text-slate-600 font-medium">
                    No student records were found for this period.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherSchedule;
